import { Tabs } from '@base-ui/react/tabs';
import type { ReactNode } from 'react';

import { HealthThemeTabs } from './components/HealthThemeTabs';
import { useAwpDashboardParams } from './useAwpDashboardParams';
import { useThemeSummaries } from './useThemeSummaries';

export interface AwpDashboardProps {
  /**
   * Whose results to show: the signed-in member's id on the member portal, the chosen patient's on the physician's.
   * The physician portal can't mount it yet: the visit list is member-only until a provider-callable visits query
   * exists (see `useAwpVisits`).
   */
  memberId: string | undefined;
  audience: 'member' | 'physician';
  /** The selected theme's content, rendered in the panel the tiles control. */
  children?: ReactNode;
}

/**
 * The AWP dashboard: the health-theme strip and the panel it controls, wired to the shared data layer and to
 * the URL. The caller only says whose dashboard it is; counts, loading and retry come from `useThemeSummaries`,
 * and the selected theme from `?theme`, so Back and shared links land on the same theme in either portal.
 */
export function AwpDashboard({ memberId, audience, children }: AwpDashboardProps) {
  const { themes, outOfRangeTotal, status, retry } = useThemeSummaries(memberId);
  const { theme, selectTheme } = useAwpDashboardParams();

  return (
    <HealthThemeTabs
      summaries={themes}
      outOfRangeTotal={outOfRangeTotal}
      activeTheme={theme}
      onSelect={selectTheme}
      audience={audience}
      state={status}
      onRetry={retry}
    >
      {/* One panel whose value follows the selection, so Base UI labels it by whichever tile is selected. */}
      <Tabs.Panel
        value={theme}
        className="mt-6 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        {children}
      </Tabs.Panel>
    </HealthThemeTabs>
  );
}
